<script setup lang="ts">
const props = withDefaults(defineProps<{
  data: unknown
  label?: string
  theme?: 'light' | 'dark'
  level?: number
  defaultOpen?: number
  trailingComma?: boolean
}>(), {
  theme: 'dark',
  level: 0,
  defaultOpen: 2,
  trailingComma: false,
})

const copied = ref(false)
const copyNode = async (e: MouseEvent) => {
  e.stopPropagation()
  const v = props.data
  const text = v === undefined ? 'undefined' : JSON.stringify(v, null, 2)
  try {
    await navigator.clipboard.writeText(text)
  } catch {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    document.execCommand('copy')
    document.body.removeChild(ta)
  }
  copied.value = true
  setTimeout(() => { copied.value = false }, 2000)
}

const isOpen = ref(props.level < props.defaultOpen)
const toggle = () => { isOpen.value = !isOpen.value }

const isObj = computed(() => props.data !== null && typeof props.data === 'object')
const isArr = computed(() => Array.isArray(props.data))
const entries = computed(() => {
  if (!isObj.value) return []
  if (isArr.value) return (props.data as unknown[]).map((v, i) => [String(i), v] as const)
  return Object.entries(props.data as Record<string, unknown>)
})
const bracket = computed(() => isArr.value ? ']' : '}')
const preview = computed(() => {
  if (!isObj.value) return ''
  const n = isArr.value ? (props.data as unknown[]).length : Object.keys(props.data as object).length
  return `${isArr.value ? '[' : '{'}…${isArr.value ? ']' : '}'} (${n})`
})

const leafType = computed(() => {
  if (props.data === null) return 'null'
  const t = typeof props.data
  if (t === 'string') return 'string'
  if (t === 'number') return 'number'
  if (t === 'boolean') return 'boolean'
  return 'other'
})
const leafText = computed(() => {
  if (props.data === null) return 'null'
  if (typeof props.data === 'string') return `"${props.data}"`
  return String(props.data)
})

const iconColor = computed(() => props.theme === 'dark' ? 'text-[#676e95] hover:text-[#a0d0a0]' : 'text-[#999] hover:text-[#333]')
const commaColor = computed(() => props.theme === 'dark' ? 'text-[#676e95]' : 'text-[#999]')
</script>

<template>
  <div class="json-node leading-[1.6] group">
    <!-- arrow + copy icon + key 绑定不换行 -->
    <span class="whitespace-nowrap">
      <span v-if="isObj" class="inline-block w-[14px] shrink-0 cursor-pointer select-none align-top" @click="toggle">
        <svg class="size-[10px] mt-[5px] transition-transform" :class="isOpen ? 'rotate-90' : ''" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
          <polyline points="9 6 15 12 9 18" />
        </svg>
      </span>
      <span v-else class="inline-block w-[14px] shrink-0" />

      <span class="mr-[4px] inline-flex size-[11px] shrink-0 items-center justify-center align-middle">
        <svg v-if="copied" class="size-[11px] text-[#49cc90]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <svg v-else class="size-[11px] cursor-pointer opacity-0 transition-opacity group-hover:opacity-100" :class="iconColor" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" @click="copyNode">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      </span>

      <span v-if="label !== undefined" :class="theme === 'dark' ? 'text-[#c792ea]' : 'text-[#6f42c1]'">{{ label }}:</span>
    </span>

    <template v-if="isObj">
      <span class="cursor-pointer" :class="theme === 'dark' ? 'text-[#89ddff]' : 'text-[#0550ae]'" @click="toggle">{{ isOpen ? (isArr ? '[' : '{') : preview }}</span>
      <template v-if="isOpen">
        <div v-for="([k, v], i) in entries" :key="k" class="pl-[18px]">
          <JsonNode :data="v" :label="String(k)" :theme="theme" :level="level + 1" :default-open="defaultOpen" :trailing-comma="i < entries.length - 1" />
        </div>
        <span :class="theme === 'dark' ? 'text-[#89ddff]' : 'text-[#0550ae]'">{{ bracket }}</span>
      </template>
    </template>

    <span v-else :class="{
      'text-[#0969da]': theme === 'light' && leafType === 'string',
      'text-[#0550ae]': theme === 'light' && (leafType === 'number' || leafType === 'other'),
      'text-[#cf222e]': theme === 'light' && leafType === 'boolean',
      'text-[#6e7781]': theme === 'light' && leafType === 'null',
      'text-[#c3e88d]': theme === 'dark' && leafType === 'string',
      'text-[#f78c6c]': theme === 'dark' && (leafType === 'number' || leafType === 'other'),
      'text-[#ff5572]': theme === 'dark' && leafType === 'boolean',
      'text-[#676e95]': theme === 'dark' && leafType === 'null',
    }">{{ leafText }}</span>

    <span v-if="trailingComma" :class="commaColor">,</span>
  </div>
</template>
